import React from 'react';
import { X, Star, FileText, Folder, Target, Zap, Lock } from 'lucide-react';
import { ABLE_COLORS, ACTIONS, CONTEXT_OPTIONS } from '../constants';
import { isTaskBlocked, getBlockingTasks } from '../utils/taskHelpers';
import AnimatedCheckbox from './AnimatedCheckbox';
import Tooltip from './Tooltip';

export default function TaskCard({
  task,
  tasks,
  expandedTaskId,
  setExpandedTaskId,
  editBuffer,
  setEditBuffer,
  contextOptions,
  markDone,
  toggleStar,
  deleteTask,
  updateTask,
  handleDragStart
}) {
  const isExpanded = expandedTaskId === task.id;

  const expandTask = (task) => {
    setExpandedTaskId(task.id);
    setEditBuffer({...task});
  };

  const saveInlineEdit = () => {
    if (!editBuffer) return;
    updateTask(expandedTaskId, editBuffer);
    setExpandedTaskId(null);
    setEditBuffer(null);
  };

  const cancelInlineEdit = () => {
    setExpandedTaskId(null);
    setEditBuffer(null);
  };

  const updateEditBuffer = (field, value) => {
    setEditBuffer({...editBuffer, [field]: value});
  };

  const blocked = isTaskBlocked(task, tasks);

  return (
    <div
      key={task.id}
      style={{
        padding: '20px',
        background: isExpanded
          ? ABLE_COLORS.neutral
          : blocked
            ? ABLE_COLORS.semantic.blockedBg
            : ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        marginBottom: '12px',
        border: isExpanded
          ? `2px solid ${ABLE_COLORS.primary}`
          : blocked
            ? `2px solid ${ABLE_COLORS.semantic.blocked}`
            : task.starred
              ? `2px solid ${ABLE_COLORS.warm}`
              : `2px solid ${ABLE_COLORS.borderLight}`,
        transition: 'all 0.3s ease',
        opacity: 1,
        boxShadow: isExpanded
          ? ABLE_COLORS.cardShadowElevated
          : ABLE_COLORS.cardShadow,
        cursor: isExpanded ? 'default' : 'pointer'
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.boxShadow = ABLE_COLORS.cardShadowHover;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.boxShadow = ABLE_COLORS.cardShadow;
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Collapsed View - Always Visible */}
      <div
        draggable={!isExpanded}
        onDragStart={(e) => !isExpanded && handleDragStart(e, task.id)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '8px',
          cursor: isExpanded ? 'default' : 'grab'
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <AnimatedCheckbox
                checked={task.done}
                onChange={(checked) => {
                  if (task.done) {
                    const updatedTask = { ...task, done: false };
                    updateTask(task.id, updatedTask);
                  } else {
                    markDone(task.id);
                  }
                }}
                size={18}
                disabled={blocked}
              />
              {blocked && (
                <Tooltip
                  content={`Blocked by: ${getBlockingTasks(task, tasks).map(t => t.title).join(', ')}`}
                  position="top"
                >
                  <Lock
                    size={16}
                    color={ABLE_COLORS.semantic.blocked}
                    style={{ cursor: 'help' }}
                  />
                </Tooltip>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleStar(task.id);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Star 
                  size={20} 
                  fill={task.starred ? ABLE_COLORS.warm : 'none'} 
                  color={task.starred ? ABLE_COLORS.warm : ABLE_COLORS.textMuted} 
                />
              </button>
            </div>
            <span
              onClick={() => !isExpanded && expandTask(task)}
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: blocked ? ABLE_COLORS.semantic.blocked : ABLE_COLORS.textPrimary,
                textDecoration: task.done ? 'line-through' : 'none',
                opacity: task.done ? 0.6 : 1,
                cursor: isExpanded ? 'default' : 'pointer',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {task.title}
            </span>
            {task.deadline && (
              <span className="task-deadline" style={{
                fontSize: '12px',
                background: ABLE_COLORS.accent,
                color: 'white',
                padding: '4px 10px',
                borderRadius: ABLE_COLORS.radiusSmall,
                fontWeight: '600',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}>
                {new Date(task.deadline).toLocaleDateString()}
              </span>
            )}
          </div>
          {blocked && (
            <div style={{
              fontSize: '12px',
              color: ABLE_COLORS.semantic.blocked,
              marginTop: '4px',
              fontWeight: '500'
            }}>
              Blocked by: {getBlockingTasks(task, tasks).map(t => t.title).join(', ')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!isExpanded && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  expandTask(task);
                }}
                style={{
                  background: ABLE_COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 2px 8px ${ABLE_COLORS.primary}30`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${ABLE_COLORS.primary}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${ABLE_COLORS.primary}30`;
                }}
              >
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete "${task.title}"? This cannot be undone.`)) {
                    deleteTask(task.id);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: ABLE_COLORS.textMuted,
                  padding: '4px'
                }}
              >
                <X size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded View - Only When isExpanded */}
      {isExpanded && editBuffer && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${ABLE_COLORS.border}` }}>
          {/* BASIC INFO Section */}
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: ABLE_COLORS.bgSubtle,
            borderRadius: ABLE_COLORS.radiusMedium
          }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: ABLE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FileText size={14} color={ABLE_COLORS.textMuted} /> Basic Info
            </div>
          
          {/* Title Input */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: ABLE_COLORS.textSecondary, marginBottom: '4px' }}>
              Task Title
            </label>
            <input
              type="text"
              value={editBuffer.title}
              onChange={(e) => updateEditBuffer('title', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Notes Textarea */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: ABLE_COLORS.textSecondary, marginBottom: '4px' }}>
              Notes
            </label>
            <textarea
              value={editBuffer.notes || ''}
              onChange={(e) => updateEditBuffer('notes', e.target.value)}
              placeholder="Add notes..."
              rows={2}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          </div>

          {/* AREA & PROJECT Section */}
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: ABLE_COLORS.bgSubtle,
            borderRadius: ABLE_COLORS.radiusMedium
          }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: ABLE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Folder size={14} color={ABLE_COLORS.textMuted} /> Area & Project
            </div>
          
          {/* Area Quick Tags */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {CONTEXT_OPTIONS.area.map(area => (
                <button
                  key={area}
                  onClick={() => updateEditBuffer('area', editBuffer.area === area ? null : area)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.area === area ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                    color: editBuffer.area === area ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.area === area ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>
          
          {/* Project Details - Stacks on mobile */}
          <div style={{ marginBottom: '12px' }}>
            <div className="task-project-row" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {/* Project */}
              <div className="task-project-field" style={{ flex: 1, minWidth: '140px' }}>
                <input
                  list="inline-project-options"
                  type="text"
                  value={editBuffer.project || ''}
                  onChange={(e) => updateEditBuffer('project', e.target.value || null)}
                  placeholder="Project"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <datalist id="inline-project-options">
                  {contextOptions.project?.map(option => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </div>
              {/* Release */}
              <div className="task-project-field" style={{ flex: 1, minWidth: '140px' }}>
                <input
                  list="inline-release-options"
                  type="text"
                  value={editBuffer.release || ''}
                  onChange={(e) => updateEditBuffer('release', e.target.value || null)}
                  placeholder="Release"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <datalist id="inline-release-options">
                  {contextOptions.release?.map(option => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </div>
              {/* Workflow */}
              <div className="task-project-field" style={{ flex: 1, minWidth: '140px' }}>
                <input
                  list="inline-workflow-options"
                  type="text"
                  value={editBuffer.workflow || ''}
                  onChange={(e) => updateEditBuffer('workflow', e.target.value || null)}
                  placeholder="Workflow"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <datalist id="inline-workflow-options">
                  {contextOptions.workflow?.map(option => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </div>
            </div>
            <style>{`
              @media (max-width: 500px) {
                .task-project-field { flex-basis: 100% !important; }
              }
            `}</style>
          </div>
          </div>

          {/* PRIORITY Section */}
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: ABLE_COLORS.bgSubtle,
            borderRadius: ABLE_COLORS.radiusMedium
          }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: ABLE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Target size={14} color={ABLE_COLORS.textMuted} /> Priority
            </div>

          {/* Priority Buttons */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {ACTIONS.map(action => (
                <button
                  key={action}
                  onClick={() => updateEditBuffer('priority', action)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.priority === action ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
                    color: editBuffer.priority === action ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.priority === action ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
          </div>

          {/* ENERGY CONTEXT Section */}
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: ABLE_COLORS.bgSubtle,
            borderRadius: ABLE_COLORS.radiusMedium
          }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: ABLE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={14} color={ABLE_COLORS.textMuted} /> Energy Context
            </div>

          {/* Time of Day Quick Tags */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {CONTEXT_OPTIONS.timeOfDay.map(time => (
                <button
                  key={time}
                  onClick={() => updateEditBuffer('timeOfDay', editBuffer.timeOfDay === time ? null : time)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.timeOfDay === time ? ABLE_COLORS.energy.timeOfDay : ABLE_COLORS.neutral,
                    color: editBuffer.timeOfDay === time ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.timeOfDay === time ? ABLE_COLORS.energy.timeOfDay : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>

          {/* Physical Quick Tags */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {CONTEXT_OPTIONS.physical.map(phys => (
                <button
                  key={phys}
                  onClick={() => updateEditBuffer('physical', editBuffer.physical === phys ? null : phys)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.physical === phys ? ABLE_COLORS.energy.physical : ABLE_COLORS.neutral,
                    color: editBuffer.physical === phys ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.physical === phys ? ABLE_COLORS.energy.physical : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {phys}
                </button>
              ))}
            </div>
          </div>

          {/* Mental Quick Tags */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {CONTEXT_OPTIONS.mental.map(ment => (
                <button
                  key={ment}
                  onClick={() => updateEditBuffer('mental', editBuffer.mental === ment ? null : ment)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.mental === ment ? ABLE_COLORS.energy.mental : ABLE_COLORS.neutral,
                    color: editBuffer.mental === ment ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.mental === ment ? ABLE_COLORS.energy.mental : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {ment}
                </button>
              ))}
            </div>
          </div>

          {/* Social Quick Tags */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {CONTEXT_OPTIONS.social.map(soc => (
                <button
                  key={soc}
                  onClick={() => updateEditBuffer('social', editBuffer.social === soc ? null : soc)}
                  style={{
                    padding: '6px 12px',
                    background: editBuffer.social === soc ? ABLE_COLORS.energy.social : ABLE_COLORS.neutral,
                    color: editBuffer.social === soc ? 'white' : ABLE_COLORS.textSecondary,
                    border: `2px solid ${editBuffer.social === soc ? ABLE_COLORS.energy.social : ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusSmall,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {soc}
                </button>
              ))}
            </div>
          </div>
          </div>

          {/* Save/Cancel Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={cancelInlineEdit}
              style={{
                padding: '10px 20px',
                background: ABLE_COLORS.neutral,
                color: ABLE_COLORS.textSecondary,
                border: `2px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveInlineEdit}
              style={{
                padding: '10px 20px',
                background: ABLE_COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: `0 4px 12px ${ABLE_COLORS.primary}30`,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 6px 16px ${ABLE_COLORS.primary}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${ABLE_COLORS.primary}30`;
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
